/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { useMobileHeaderAction } from "@/context/tabNavigation";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, SkeletonRows } from "@/components/common/States";
import { DeleteConfirmationDialog } from "@/components/common/DeleteConfirmationDialog";
import { showToast } from "@/components/common/Toast";
import { DockerHostForm } from "./DockerHostForm";
import { HostView } from "./portainer/HostView";
import { EnvironmentsHome } from "./portainer/EnvironmentsHome";
import { useFocus, useReportSelected } from "@/utils/sidebarFocus";
import {
  getDockerHosts,
  createDockerHost,
  updateDockerHost,
  deleteDockerHost,
  DockerHost,
  CreateDockerHostRequest,
  UpdateDockerHostRequest,
} from "@/api/docker";
import { DOCKER_REFRESH_INTERVALS } from "@/constants/dockerRefreshIntervals";

const DEFAULT_POLL_INTERVAL_SECONDS = 30;

export const DockerTab: React.FC = () => {
  const [selectedHost, setSelectedHost] = useState<DockerHost | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingHost, setEditingHost] = useState<DockerHost | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hostToDelete, setHostToDelete] = useState<DockerHost | null>(null);
  const queryClient = useQueryClient();

  const { data: hosts = [], isLoading } = useQuery({
    queryKey: ["docker-hosts"],
    queryFn: getDockerHosts,
    refetchInterval: DOCKER_REFRESH_INTERVALS.HOSTS_LIST,
  });

  // Most users will only have one Docker host (the local socket) -- skip
  // straight to its container list instead of making them pick from a list
  // of one.
  useEffect(() => {
    if (!selectedHost && hosts.length === 1) {
      setSelectedHost(hosts[0]);
    }
  }, [hosts, selectedHost]);

  useReportSelected("docker", selectedHost?.id);

  // Sidebar sub-item clicks select a specific Docker host.
  useFocus("docker", (id) => {
    const h = hosts.find((x) => x.id === parseInt(id, 10));
    if (h) setSelectedHost(h);
  });

  // Mobile: surface "Add host" in the top bar (only on the hosts list view).
  useMobileHeaderAction(
    () =>
      selectedHost ? null : (
        <Button
          onClick={() => {
            setEditingHost(null);
            setIsFormOpen(true);
          }}
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-600 dark:text-gray-400"
          aria-label="Add Docker host"
        >
          <PlusIcon className="h-5 w-5" />
        </Button>
      ),
    [selectedHost]
  );

  const createMutation = useMutation({
    mutationFn: createDockerHost,
    onSuccess: (newHost) => {
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] });
      setIsFormOpen(false);
      showToast("Docker host added", "success", {
        description: `${newHost.name} has been added successfully`,
      });
    },
    onError: (error: Error) => {
      showToast("Failed to add docker host", "error", { description: error.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDockerHostRequest }) =>
      updateDockerHost(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] });
      setIsFormOpen(false);
      setEditingHost(null);
      showToast("Docker host updated", "success");
    },
    onError: (error: Error) => {
      showToast("Failed to update docker host", "error", { description: error.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDockerHost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["docker-hosts"] });
      setSelectedHost(null);
      showToast("Docker host deleted", "success", {
        description: `${hostToDelete?.name || "Host"} has been removed`,
      });
    },
    onError: (error: Error) => {
      showToast("Failed to delete docker host", "error", { description: error.message });
    },
  });

  const handleFormSubmit = (data: CreateDockerHostRequest) => {
    if (editingHost) {
      updateMutation.mutate({ id: editingHost.id, data: { ...data, id: editingHost.id } });
    } else {
      createMutation.mutate(data);
    }
  };

  const forms = (
    <>
      <DockerHostForm
        host={editingHost}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setEditingHost(null);
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        isOpen={isFormOpen}
        defaultPollIntervalSeconds={DEFAULT_POLL_INTERVAL_SECONDS}
      />
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setHostToDelete(null);
        }}
        onConfirm={() => {
          if (hostToDelete) {
            setDeleteDialogOpen(false);
            deleteMutation.mutate(hostToDelete.id);
            setHostToDelete(null);
          }
        }}
        itemName={hostToDelete?.name || ""}
        isDeleting={deleteMutation.isPending}
      />
    </>
  );

  const openAdd = () => {
    setEditingHost(null);
    setIsFormOpen(true);
  };

  if (selectedHost) {
    return (
      <>
        <HostView
          key={selectedHost.id}
          host={selectedHost}
          canGoBack={hosts.length > 1}
          onBack={() => setSelectedHost(null)}
          onEdit={() => {
            setEditingHost(selectedHost);
            setIsFormOpen(true);
          }}
          onDelete={() => {
            setHostToDelete(selectedHost);
            setDeleteDialogOpen(true);
          }}
        />
        {forms}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Docker"
        subtitle={`${hosts.length} ${hosts.length === 1 ? "environment" : "environments"} · pick one to manage its containers, stacks, images, volumes and networks`}
        actions={
          <Button onClick={openAdd} variant="default" size="default">
            Add Docker Host
          </Button>
        }
      />
      {isLoading ? (
        <SkeletonRows rows={4} className="p-4" />
      ) : hosts.length === 0 ? (
        <EmptyState
          icon={<ServerIcon className="h-8 w-8" />}
          title="No Docker hosts configured"
          hint="Add a host (usually the local socket) to start monitoring containers"
          action={<Button onClick={openAdd}>Add Docker Host</Button>}
        />
      ) : (
        <div className="gh sv dkr">
          <EnvironmentsHome hosts={hosts} onSelect={setSelectedHost} />
        </div>
      )}
      {forms}
    </div>
  );
};
